/**
 * 添加到购物车按钮
 *  示例：<btn-cart items='{"items": [{"id": "46715539947755", "quantity": 1}, {"id": "46715541946603", "quantity": 2}]}'></btn-cart>
 *  - 【使用注意】：需要在其他地方更新items属性，其他不用操作，点击按钮自动完成加购物车交互
 *  - 样式在外面包裹一层，可任意自定义
 *  - 状态：idle | loading
 *  - 自动刷新 cart 数量
 * @class BtnCart
 * @extends {HTMLElement}
 */
class BtnCart extends HTMLElement {
  static observedAttributes = ['items', 'add-status'] // 必须全小写

  constructor () {
    super()
    this._connected = false
    this._defaultContent = ''
    this._statusEnum = {
      idle: 'idle',
      loading: 'loading'
    }

    this.apiPath = {
      addCard: '/cart/add.js',
      getCard: '/cart.js'
    }
    this.headers = { 'Content-Type': 'application/json' }

    this._addToCart = this.addToCart.bind(this)

    this.initBtnAttrs()
    this.initInnerContent()
  }

  // *** start: 生命周期方法
  // 自定义元素添加至页面
  connectedCallback () {
    if (this._connected) return // 防止重复添加
    this._connected = true

    this.addEventListener('click', this._addToCart)
  }
  // disconnectedCallback() {
  //   console.log("自定义元素从页面中移除。")
  // }
  // adoptedCallback() {
  //   console.log("自定义元素移动至新页面。")
  // }
  attributeChangedCallback (name, oldValue, newValue) {
    console.log(`属性 ${name} 已由 ${oldValue} 变更为 ${newValue}。`)
  }
  // *** end: 生命周期方法

  // 初始化基本样式、属性
  initBtnAttrs = () => {
    this.setAttribute('add-status', 'idle') // idle | loading | success | error
    this.classList.add('cursor-pointer', 'w-full', 'h-full')
  }

  // 创建内部内容
  initInnerContent () {
    // 保存原始内容作为默认文案
    this._defaultContent = this.innerHTML.trim() || ''

    // 创建不同状态的容器
    const container = document.createElement('div')
    container.className = 'flex-x-center tw-gap-2 md:tw-gap-4 btn-cart-container'

    // 创建样式
    const style = document.createElement('style')
    style.textContent = `
      btn-cart[add-status="${this._statusEnum.idle}"] {
        .status-content.${this._statusEnum.idle} { display: flex; }
        .status-content.${this._statusEnum.loading} { display: none; }
      }
      btn-cart[add-status="${this._statusEnum.loading}"] {
        cursor: not-allowed;
        pointer-events: none;
        .status-content.${this._statusEnum.idle} { display: none; }
        .status-content.${this._statusEnum.loading} { display: flex; }
      }
      .btn-cart-container {
        width: 100%;
        height: 100%;
      }
      .status-content {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `

    // 创建各个状态的内容元素
    this.idleEl = document.createElement('span')
    this.idleEl.className = `flex-x-center tw-gap-2 md:tw-gap-4 status-content ${this._statusEnum.idle}`
    this.idleEl.innerHTML = this._defaultContent

    this.loadingEl = document.createElement('span')
    this.loadingEl.className = `flex-x-center tw-gap-2 md:tw-gap-4 status-content ${this._statusEnum.loading}`
    // this.loadingEl.textContent = 'Loading ...'
    this.loadingEl.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0538/9533/2027/files/loading-spinner.gif?v=1769841918" width="24" height="24" alt="Loading..." />'

    // 清空原内容并添加新结构
    this.innerHTML = ''
    this.appendChild(style)
    this.appendChild(container)
    container.appendChild(this.idleEl)
    container.appendChild(this.loadingEl)
  }


  // 添加到购物车
  async addToCart () {
    console.log('BtnCart addToCart')
    try {
      let formData = this.getAttribute('items')
      console.log('formData>>', formData)
      if (!formData || formData.length === 0) {
        console.error('items 参数不能为空')
        return
      }
      if (this.getAttribute('add-status') === 'loading') {
        console.error('正在添加到购物车...')
        return
      }

      this.setAttribute('add-status', 'loading')

      // 解析 formData 获取第一个 variant ID（用于显示商品信息）
      let parsedData;
      try {
        parsedData = JSON.parse(formData);
      } catch (e) {
        console.error('解析 items 数据失败:', e);
        throw new Error('items 参数格式错误');
      }

      // 获取第一个商品的 variant ID（用于 sections_url）
      const firstVariantId = parsedData.items?.[0]?.id || parsedData.id;
      if (!firstVariantId) {
        console.error('无法获取 variant ID');
        throw new Error('items 数据中缺少 id');
      }

      // 获取需要更新的 sections（用于购物车弹窗和抽屉）
      // 初始化时就包含 variant-added，这是显示添加商品信息的必需 section
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      // 构建请求 URL，包含 sections 和 sections_url 参数
      let addCartUrl = this.apiPath.addCard;
      const urlParams = [];
      if (sectionsToBundle.length > 0) {
        urlParams.push('sections=' + sectionsToBundle.join(','));
      }
      // sections_url 告诉 Shopify 使用哪个 variant 的信息来渲染 sections
      urlParams.push('sections_url=' + encodeURIComponent(`${window.Shopify?.routes?.root || '/'}variants/${firstVariantId}`));
      
      if (urlParams.length > 0) {
        addCartUrl += '?' + urlParams.join('&');
      }

      console.log(`%c>> 请求 URL:`, 'color:blue', addCartUrl)

      const response = await fetch(addCartUrl, {
        method: 'POST',
        headers: this.headers,
        body: formData
      })

      const responseJson = await response.json()
      console.log(`%c>> 添加购物车响应`, 'color:yellow', responseJson)
      console.log(`%c>> 收集到的 sections:`, 'color:cyan', sectionsToBundle)
      
      if (response.ok) {
        this.updateCart()
        
        // 获取完整的购物车信息
        const cartContent = await (await fetch(`${this.apiPath.getCard}`)).json();
        console.log(`%c>> 购物车完整信息`, 'color:yellow', cartContent)
        
        // 将 sections 数据合并到 cartContent（如果存在）
        if (responseJson.sections) {
          // 处理 sections 数据，确保每个 section 都被 .shopify-section 包裹
          const processedSections = {};
          Object.keys(responseJson.sections).forEach(key => {
            const sectionHtml = responseJson.sections[key];
            // 检查 HTML 是否已经包含 .shopify-section
            if (sectionHtml && !sectionHtml.includes('class="shopify-section"')) {
              // 如果没有，则添加包裹
              processedSections[key] = `<div class="shopify-section">${sectionHtml}</div>`;
            } else {
              processedSections[key] = sectionHtml;
            }
          });
          
          cartContent['sections'] = processedSections;
          console.log(`%c>> 处理后的 sections:`, 'color:green', Object.keys(processedSections))
        } else {
          console.warn(`%c>> 警告: responseJson 中没有 sections 数据！`, 'color:red')
        }
        
        // 触发 variant:add 事件
        document.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
            cart: cartContent
          }
        }));
        
        // 触发 cart:change 事件
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        }));
        
      } else {
        // 处理错误
        const errorData = await response.json()
        throw new Error(errorData.description || '添加到购物车失败')
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      this.setAttribute('add-status', 'idle')
    }
  }

  // 更新购物车
  updateCart = async () => {
    console.log('BtnCart updateCart')

    document.dispatchEvent(new CustomEvent('cart:refresh'))
  }
}

window.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('btn-cart')) {
    window.customElements.define('btn-cart', BtnCart)
  }
})
